// @flow
import React, { useState } from "react";
import Card from "@material-ui/core/Card";
import Box from "@material-ui/core/Box";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import type {
  Game,
  TicketReservationStatusType,
} from "../../common/types";
import GameDetails from "../GameDetails/GameDetails";
import Grid from "@material-ui/core/Grid";
import TeamLogo from "../../components/TeamLogo";
import formatDates from "../../common/formatDates";
import gameSelectors from "../../selectors/gameSelectors";
import ScheduleGameCardFooter from "./ScheduleGameCardFooter";
import ticketReservationSelectors from "../../selectors/ticketReservationSelectors";
import Countdown from "react-countdown";
import Label from "../../components/Label";
import { useFirestore, useFirestoreDocDataOnce } from "reactfire";
import ScheduleGameCardResults from "./ScheduleGameCardResults";

type Props = {
  game: Game,
  type?: "MATCH_DAY" | "UPCOMING" | "RESULT",
};

function ScheduleGameCard({ game, type }: Props) {
  const { date, teamId, opponentId, locationId } = game;
  const firestore = useFirestore();
  const [showDetails, setShowDetails] = useState(false);

  const team = useFirestoreDocDataOnce(
    firestore.collection("teams").doc(teamId)
  );
  const opponent = useFirestoreDocDataOnce(
    firestore.collection("opponents").doc(opponentId)
  );
  const location = useFirestoreDocDataOnce(
    firestore.collection("locations").doc(locationId)
  );

  const { leftTeam, rightTeam } = gameSelectors.getLeftAndRightTeams(
    game,
    team,
    opponent
  );

  const withTickets =
    game.isHomegame && team.visitorsAllowed && location.visitorsAllowed;

  const ticketReservationStatus: TicketReservationStatusType = !withTickets
    ? "NONE"
    : ticketReservationSelectors.getTicketReservationStatus(game);

  const showGameDetails = () => setShowDetails(true);

  const DateInfo = () =>
    type === "MATCH_DAY" ? (
      <>
        <Typography
          variant="caption"
          color="textSecondary"
          component="p"
          align="center"
        >
          {formatDates.dayAndDate(date)}
        </Typography>
        <Typography variant="h5" component="p" align="center">
          <b>{formatDates.time(date)} Uhr</b>
        </Typography>
        <Box my={0.5} style={{ textAlign: "center" }}>
          <Countdown
            date={date}
            renderer={({ hours, minutes, seconds, completed }) => {
              if (completed) {
                return <Label color="danger" label="Bereits gestartet" />;
              } else {
                // Render a countdown
                const label = `Start in ${hours < 10 && hours > 0 ? "0" : ""}${
                  hours > 0 ? `${hours}:` : ""
                }${minutes < 10 ? "0" : ""}${minutes}:${
                  seconds < 10 ? "0" : ""
                }${seconds}`;
                return <Label label={label} color="success" />;
              }
            }}
          />
        </Box><Typography
        variant="caption"
        align="center"
        color="textSecondary"
        component="p"
      >
        {location.name}
      </Typography>
      </>
    ) : type === "UPCOMING" ? (
      <>
        <Typography variant="h5" component="p" align="center">
          <b>{formatDates.dayAndDate(date)}</b>
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          component="p"
          align="center"
        >
          {formatDates.time(date)} Uhr
        </Typography><Typography
        variant="caption"
        align="center"
        color="textSecondary"
        component="p"
      >
        {location.name}
      </Typography>
      </>
    ) : type === "RESULT" ? (
      <ScheduleGameCardResults game={game} />
    ) : null;

  return (
    <>
      <Card elevation={1}>
        <CardActionArea onClick={showGameDetails}>
          <CardContent>
            <Grid
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              spacing={1}
              wrap="nowrap"
            >
              <Grid item xs={3}>
                <Box mb={1}>
                  <TeamLogo
                    iconString={leftTeam.iconString}
                    name={leftTeam.name}
                  />
                </Box>
                <Typography
                  align="center"
                  variant="caption"
                  color="textSecondary"
                  component="p"
                  noWrap
                >
                  {leftTeam.name}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <DateInfo />
              </Grid>
              <Grid item xs={3}>
                <Box mb={1}>
                  <TeamLogo
                    iconString={rightTeam.iconString}
                    name={rightTeam.name}
                  />
                </Box>
                <Typography
                  align="center"
                  variant="caption"
                  color="textSecondary"
                  component="p"
                  noWrap
                >
                  {rightTeam.name}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </CardActionArea>
        {ticketReservationStatus !== "NONE" &&
          ticketReservationStatus !== "SCHEDULED" &&
          type !== "RESULT" && (
            <ScheduleGameCardFooter
              location={location}
              game={game}
              showGameDetails={showGameDetails}
              ticketReservationStatus={ticketReservationStatus}
              type={type}
            />
          )}
      </Card>
      <GameDetails
        type={type}
        game={game}
        team={team}
        opponent={opponent}
        location={location}
        open={showDetails}
        setOpen={setShowDetails}
        ticketReservationStatus={ticketReservationStatus}
      />
    </>
  );
}

export default ScheduleGameCard;
